import React from 'react'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  selectCrossClientFilteredResults,
  selectCrossClientResultsExecution,
  setCrossClientPage,
  setCrossClientPageSize,
} from '../../../store/crossClient/crossClientResults'
import { ZocksDataTable } from '../../../components/DataTable/ZocksDataTable'
import { TableColumn, TableStyles } from 'react-data-table-component'
import { CrossClientQueryResultDisplay } from '../../../models/CrossClient'
import { CrossClientQueryResultMatchCell } from './Cells/CrossClientQueryResultMatchCell'
import { CrossClientQueryResultClientCell } from './Cells/CrossClientQueryResultClientCell'
import { CrossClientQueryResultOwnerCell } from './Cells/CrossClientQueryResultOwnerCell'
import { CrossClientResultDetailsModal } from './Details/CrossClientResultDetailsModal'
import { useCrossClientQueryResultDetails } from './Details/hooks'
import { CrossClientResultButtons } from './Details/CrossClientResultButtons'
import './CrossClientTable.scss'

const TABLE_STYLES: TableStyles = {
  table: {
    style: {
      border: '1px solid var(--zocks-gray-200)',
      borderRadius: '1rem',
    },
  },
  headRow: {
    style: {
      background: 'var(--zocks-gray-200)',
      border: '1px solid var(--zocks-gray-200)',
      borderRadius: '1rem 1rem 0 0',
    },
  },
  headCells: {
    style: {
      color: 'var(--zocks-gray-700)',
      textTransform: 'uppercase',
      fontWeight: '700',
    },
  },
  rows: {
    style: {
      animation: 'row-in 0.4s ease-out forwards',
      '&:hover': {
        background: 'var(--zocks-gray-100) !important',
      },
      '&:not(:last-of-type)': {
        borderBottomColor: 'var(--zocks-gray-200) !important',
      },
    },
  },
  noData: {
    style: {
      borderRadius: '1rem',
    },
  },
}

export const CrossClientTable = () => {
  const dispatch = useAppDispatch()

  const execution = useAppSelector(selectCrossClientResultsExecution)
  const results = useAppSelector(selectCrossClientFilteredResults)

  const selectedResultDetails = useCrossClientQueryResultDetails()
  const { handleShowResultDetails } = selectedResultDetails

  const { decomposition } = execution ?? {}

  if (!decomposition) {
    return <div>Unable to determine decomposition for this query</div>
  }

  const columns: TableColumn<CrossClientQueryResultDisplay>[] = [
    {
      selector: (row) => row.clientId,
      name: 'Client',
      grow: 21,
      cell: (row) => <CrossClientQueryResultClientCell result={row} />,
    },
    {
      name: 'Owner',
      grow: 16,
      cell: (row) => <CrossClientQueryResultOwnerCell result={row} />,
    },
    {
      name: 'Match',
      grow: 5.5,
      cell: (row) => <CrossClientQueryResultMatchCell result={row} onShowDetails={() => handleShowResultDetails(row)} />,
    },
    {
      ignoreRowClick: true,
      grow: 6,
      center: true,
      cell: (row) => <CrossClientResultButtons result={row} />,
    },
  ]

  const handlePageChanged = (page: number) => {
    dispatch(setCrossClientPage(page))
  }

  const handlePageSizeChanged = (currentRowsPerPage: number) => {
    dispatch(setCrossClientPageSize(currentRowsPerPage))
  }

  return (
    <>
      <CrossClientResultDetailsModal resultDetails={selectedResultDetails} decomposition={decomposition} />
      <ZocksDataTable
        columns={columns}
        data={results}
        customStyles={TABLE_STYLES}
        pagination
        highlightOnHover
        className='cross-client-table'
        paginationPerPage={20}
        onChangePage={handlePageChanged}
        onChangeRowsPerPage={handlePageSizeChanged}
      />
    </>
  )
}
